// src/components/dashboard/widgets/registry.ts
import type { ComponentType } from 'react';
import { TrendingUp, PieChart, Calendar, GraduationCap, Cloud, PartyPopper, TrainFront, Sparkles, Star, StickyNote, CheckSquare, Wallet, Users } from 'lucide-react';
import WeatherWidget from './weather-widget';
import PublicHolidayWidget from './public-holiday-widget';
import SchoolHolidaysWidget from './school-holidays-widget';
import TransportWidget from './transport-widget';
import MetricCardWidget from './metric-card-widget';
import type { NearbyStop } from './nearby-transport-picker';
import NearbyEventsWidget from './nearby-events-widget';
import GoogleReviewsWidget from './google-reviews-widget';
import GoogleReviewsExtendedWidget from './google-reviews-extended-widget';
import StaffingWidget from './staffing-widget';
import type { OwnerMetrics } from '@/src/hooks/useOwnerMetrics';

export interface MetricCardData {
  label: string;
  value: string;
  sub: string;
  dot?: boolean;
  Icon: ComponentType<{ size?: number; className?: string; color?: string }>;
  onClick?: () => void;
  linkLabel?: string;
}

export interface WidgetContext {
  weather: { temp: number; description: string; icon: string } | null;
  nearbyStops: NearbyStop[];
  venueLat: number | null;
  venueLon: number | null;
  venueId: string;
  ownerMetrics: OwnerMetrics | null;
  ownerMetricsLoading: boolean;
  metrics: { revenue: MetricCardData; labour: MetricCardData; bookings: MetricCardData; training: MetricCardData };
  panels: {
    tasks: React.ReactNode;
    training: React.ReactNode;
    notes: React.ReactNode;
    snapshot: React.ReactNode;
    pendingActions: React.ReactNode;
  };
}
export interface WidgetDef {
  id: string;
  /** Only offered to venues in these states, e.g. ['NSW']. Omit = everywhere. */
  regions?: string[];
  label: string;
  icon: ComponentType<{ size?: number; className?: string; color?: string }>;
  size?: 'small' | 'third' | 'half' | 'full';
  render: (ctx: WidgetContext) => React.ReactNode;
}

const SIZE_RANK: Record<string, number> = { small: 0, third: 1, half: 2, full: 3 };
export function sizeClass(size: WidgetDef['size']): string {
  switch (size) {
    case 'third': return 'col-span-12 sm:col-span-6 lg:col-span-4';
    case 'half': return 'col-span-12 lg:col-span-6';
    case 'full': return 'col-span-12';
    default: return 'col-span-6 lg:col-span-3';
  }
}
export function widgetSizeRank(size: WidgetDef['size']): number {
  return SIZE_RANK[size ?? 'small'];
}

const STATE_NAMES: Record<string, string> = {
  'NEW SOUTH WALES': 'NSW', 'VICTORIA': 'VIC', 'QUEENSLAND': 'QLD', 'SOUTH AUSTRALIA': 'SA',
  'WESTERN AUSTRALIA': 'WA', 'TASMANIA': 'TAS', 'NORTHERN TERRITORY': 'NT', 'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
};
/** The venue's state code; falls back to timezone when no state is set. */
export function venueRegion(state?: string | null, timezone?: string | null): string | null {
  const s = (state ?? '').trim().toUpperCase();
  if (s) return STATE_NAMES[s] ?? s;
  return timezone === 'Australia/Sydney' ? 'NSW' : null;
}
export function widgetAllowed(def: WidgetDef, region: string | null): boolean {
  return !def.regions || (region != null && def.regions.includes(region));
}

export const WIDGET_REGISTRY: WidgetDef[] = [
  { id: 'revenue', label: 'Revenue', icon: TrendingUp, render: (ctx) => <MetricCardWidget {...ctx.metrics.revenue} /> },
  { id: 'labour', label: 'Labour %', icon: PieChart, render: (ctx) => <MetricCardWidget {...ctx.metrics.labour} /> },
  { id: 'bookings', label: 'Bookings', icon: Calendar, render: (ctx) => <MetricCardWidget {...ctx.metrics.bookings} /> },
  { id: 'training', label: 'Training', icon: GraduationCap, render: (ctx) => <MetricCardWidget {...ctx.metrics.training} /> },
  { id: 'staffing', label: 'Staffing (Deputy)', icon: Users, size: 'third', render: (ctx) => <StaffingWidget data={ctx.ownerMetrics} loading={ctx.ownerMetricsLoading} /> },
  { id: 'weather', label: 'Weather', icon: Cloud, render: (ctx) => <WeatherWidget weather={ctx.weather} /> },
  { id: 'public_holidays', label: 'Public Holidays', icon: PartyPopper, render: () => <PublicHolidayWidget /> },
  { id: 'school_holidays', label: 'School Holidays (NSW)', regions: ['NSW'], icon: GraduationCap, render: () => <SchoolHolidaysWidget /> },
  { id: 'transport', label: 'Transport (NSW)', regions: ['NSW'], icon: TrainFront, render: (ctx) => <TransportWidget nearbyStops={ctx.nearbyStops} /> },
  { id: 'events', label: 'Nearby Events', icon: Sparkles, render: (ctx) => <NearbyEventsWidget lat={ctx.venueLat} lon={ctx.venueLon} /> },
  { id: 'google_reviews', label: 'Google Reviews', icon: Star, render: (ctx) => <GoogleReviewsWidget venueId={ctx.venueId} /> },
  { id: 'google_reviews_extended', label: 'Google Reviews (Extended)', icon: Star, size: 'full', render: (ctx) => <GoogleReviewsExtendedWidget venueId={ctx.venueId} /> },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare, size: 'third', render: (ctx) => ctx.panels.tasks },
  { id: 'training_panel', label: 'Training (Team)', icon: GraduationCap, size: 'third', render: (ctx) => ctx.panels.training },
  { id: 'notes', label: 'Notes', icon: StickyNote, size: 'third', render: (ctx) => ctx.panels.notes },
  { id: 'snapshot', label: 'Your Snapshot', icon: TrendingUp, size: 'half', render: (ctx) => ctx.panels.snapshot },
  { id: 'pending_actions', label: 'Pending Actions', icon: CheckSquare, size: 'half', render: (ctx) => ctx.panels.pendingActions },
];

export const DEFAULT_LAYOUT = ['revenue', 'labour', 'bookings', 'staffing', 'tasks', 'training_panel', 'notes', 'snapshot', 'pending_actions'];